import { useState, useEffect } from 'react';
import { SearchLimitLeftModel } from '@/types/home';

export type GuestUsageModel = SearchLimitLeftModel & {
  search_count?: number;
  last_free_search_time?: number; // 上次免费查询的时间戳
};

const LOCAL_STORAGE_KEY = 'guest_viewer_search_usage_data';
const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24小时的毫秒数

// 默认初始值，用于 SSR 和客户端初始渲染保持一致
const DEFAULT_GUEST_USAGE: GuestUsageModel = { search_limit: 1, search_count: 0 };

const useGuestViewerSearchUsageState = (initialModel?: GuestUsageModel) => {
  // 使用固定默认值初始化，避免 hydration 错误
  const [guestUsage, setGuestUsageState] = useState<GuestUsageModel>(initialModel || DEFAULT_GUEST_USAGE);
  const [isHydrated, setIsHydrated] = useState(false);

  // 客户端 hydration 后从 localStorage 读取数据
  useEffect(() => {
    try {
      const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedData) {
        const storedUsage: GuestUsageModel = JSON.parse(storedData);

        // 检查是否已经过了24小时，是否可以重置免费次数
        if (storedUsage?.last_free_search_time) {
          const now = Date.now();
          const timeSinceLastSearch = now - storedUsage.last_free_search_time;

          // 如果已经过了24小时，重置免费次数为1
          if (timeSinceLastSearch >= ONE_DAY_MS) {
            const resetUsage = {
              ...storedUsage,
              search_limit: 1, // 重置为1次免费查询
              search_count: 0, // 重置搜索计数
            };

            // 同时更新本地存储
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resetUsage));
            setGuestUsageState(resetUsage);
          } else {
            setGuestUsageState(storedUsage);
          }
        } else {
          setGuestUsageState(storedUsage);
        }
      }
    } catch (error) {
      console.error('Failed to parse guest usage data from localStorage:', error);
    }
    setIsHydrated(true);
  }, []);

  // 包装setState，在状态更新时同步到localStorage
  const setGuestUsage = (updater: GuestUsageModel | ((prev: GuestUsageModel) => GuestUsageModel)) => {
    setGuestUsageState((prev) => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;

      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newState));
      } catch (error) {
        console.error('Failed to store guest usage data to localStorage:', error);
      }

      return newState;
    });
  };

  // 消耗一次免费查询，并记录时间
  const consumeFreeTimes = () => {
    setGuestUsage((prev) => {
      return {
        ...prev,
        search_limit: Math.max((prev?.search_limit || 0) - 1, 0),
        search_count: (prev?.search_count || 0) + 1,
        last_free_search_time: Date.now(), // 记录本次查询的时间
      };
    });
  };

  return {
    guestUsage,
    setGuestUsage,
    consumeFreeTimes,
    isHydrated,
  };
};

export default useGuestViewerSearchUsageState;
